/**
 * Pure H OS · Sprint 3 · AI Copy Assistant.
 * Composes Card, SectionHeader, Button, TextInput, Segmented, ListRow, Kbd, Field.
 */
import { useState } from "react";
import { Sparkles, Wand2, Copy, Star, StarOff, RefreshCcw } from "lucide-react";
import { Card, SectionHeader, Button, TextInput } from "../../components/lib";
import { Segmented, Field, Kbd, ListRow } from "../../components/lib.overlay";
import {
  COPY_TONES, COPY_FORMATS, COPY_HISTORY,
  CopyTone, CopyFormat, CopyDraft,
} from "../../mocks/marketingData";

const OPENERS: Record<CopyTone, string> = {
  editorial:  "A perfume, argued in slow prose.",
  poetic:     "A slow evening, translated into scent.",
  confident:  "You already own the room. This is the reason why.",
  warm:       "Something quiet, for the people who noticed you first.",
  playful:    "This bottle knows what you did last summer.",
};

function generate(brief: string, tone: CopyTone, format: CopyFormat): string {
  const opener = OPENERS[tone];
  const b = brief.trim() || "ORVAN · Fragrance brief";
  const map: Record<CopyFormat, string> = {
    "headline":       `${opener}\n\n${b}. In three words: unmistakable, unhurried, unrepeatable.`,
    "caption":        `${opener}\n\n${b}.\n\nWorn twice, remembered forever. — ORVAN`,
    "email-subject":  `${b}: a quiet thank-you, and one bottle set aside for you.`,
    "product-desc":   `${b}\n\n${opener}\n\nA composition designed to unfold slowly — top notes give way to the amber heart in the second hour, and the drydown lingers into the following morning. Bottled in Egypt. Blind-evaluated by ORVAN's R&D bench.`,
    "ad":             `${opener}\n\n${b} — now available. Free samples with your first order.`,
  };
  return map[format];
}

export default function CopyAssistant() {
  const [brief, setBrief]   = useState("");
  const [tone, setTone]     = useState<CopyTone>("poetic");
  const [format, setFormat] = useState<CopyFormat>("caption");
  const [output, setOutput] = useState<string>(generate(brief, "poetic", "caption"));
  const [history, setHistory] = useState<CopyDraft[]>(COPY_HISTORY);
  const [busy, setBusy] = useState(false);

  const run = () => {
    setBusy(true);
    setTimeout(() => {
      const next = generate(brief, tone, format);
      setOutput(next);
      const draft: CopyDraft = {
        id: `cp-${Date.now()}`,
        title: brief.slice(0, 40) || "Untitled",
        format, tone, body: next,
        createdRelative: "Just now",
        favourite: false,
      };
      setHistory((h) => [draft, ...h].slice(0, 12));
      setBusy(false);
    }, 350);
  };

  const toggleFav = (id: string) => setHistory((h) => h.map((d) => d.id === id ? { ...d, favourite: !d.favourite } : d));
  const restore = (d: CopyDraft) => {
    setBrief(d.title);
    setTone(d.tone);
    setFormat(d.format);
    setOutput(d.body);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="mkt-copy">
      <div className="lg:col-span-2 space-y-6">
        <Card testId="mkt-copy-brief">
          <SectionHeader eyebrow="Brief" title="What are we writing?" action={<Wand2 size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Brief / product / campaign" hint="One line is enough. The AI adapts to ORVAN's voice.">
            <TextInput value={brief} onChange={setBrief} testId="mkt-copy-brief-input" />
          </Field>
          <Field label="Format" inline>
            <Segmented<CopyFormat>
              testId="mkt-copy-format"
              value={format}
              onChange={setFormat}
              options={COPY_FORMATS.map((f) => ({ value: f.key, label: f.label }))}
            />
          </Field>
          <Field label="Tone" hint="Editorial for launches · Warm for loyalty · Playful for TikTok." inline>
            <Segmented<CopyTone>
              testId="mkt-copy-tone"
              value={tone}
              onChange={setTone}
              options={COPY_TONES.map((t) => ({ value: t.key, label: t.label }))}
            />
          </Field>
          <div className="pt-3 flex items-center justify-between">
            <div className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
              Shortcut: <Kbd>⌘</Kbd><Kbd>↵</Kbd> to regenerate
            </div>
            <Button onClick={run} disabled={busy} testId="mkt-copy-generate">
              {busy ? "Composing…" : <><Sparkles size={13} /> Generate</>}
            </Button>
          </div>
        </Card>

        <Card testId="mkt-copy-output">
          <SectionHeader
            eyebrow="Draft"
            title={`${COPY_FORMATS.find((f) => f.key === format)?.label} · ${COPY_TONES.find((t) => t.key === tone)?.label}`}
            action={
              <div className="flex items-center gap-1">
                <button data-testid="mkt-copy-regen" onClick={run} className="p-1 rounded" style={{ color: "var(--ph-ink-muted)" }}><RefreshCcw size={13} /></button>
                <button
                  data-testid="mkt-copy-clip"
                  onClick={() => { try { navigator.clipboard.writeText(output); } catch { /* empty */ } }}
                  className="p-1 rounded"
                  style={{ color: "var(--ph-ink-muted)" }}
                >
                  <Copy size={13} />
                </button>
              </div>
            }
          />
          <div
            className="p-4 whitespace-pre-wrap"
            style={{
              background: "var(--ph-surface)",
              borderRadius: "var(--ph-radius-md)",
              color: "var(--ph-ink-strong)",
              fontFamily: "var(--ph-display-font)",
              fontSize: "1.05rem",
              lineHeight: 1.55,
            }}
            data-testid="mkt-copy-output-body"
          >
            {output}
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card padded={false} testId="mkt-copy-history">
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
            <SectionHeader eyebrow="History" title="Recent drafts" action={<Sparkles size={13} style={{ color: "var(--ph-ai)" }} />} />
          </div>
          <div className="p-2" data-testid="mkt-copy-history-list">
            {history.length === 0 ? (
              <div className="py-6 text-center text-sm" style={{ color: "var(--ph-ink-muted)" }}>No drafts yet.</div>
            ) : history.map((d) => (
              <ListRow
                key={d.id}
                testId={`mkt-copy-hist-${d.id}`}
                onClick={() => restore(d)}
                label={d.title}
                sublabel={<>{COPY_FORMATS.find((f) => f.key === d.format)?.label} · {COPY_TONES.find((t) => t.key === d.tone)?.label} · {d.createdRelative}</>}
                trailing={
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); toggleFav(d.id); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); toggleFav(d.id); } }}
                    data-testid={`mkt-copy-fav-${d.id}`}
                    style={{ color: d.favourite ? "var(--ph-brand-primary)" : "var(--ph-ink-soft)", cursor: "pointer", display: "inline-flex" }}
                  >
                    {d.favourite ? <Star size={13} fill="currentColor" /> : <StarOff size={13} />}
                  </span>
                }
              />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
